import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getDatabase, ref, get } from 'firebase/database';
import { Trophy, Clock, Zap, Flame, Gamepad2, CheckCircle } from 'lucide-react';

interface UserMatch {
  tournamentId: string;
  game: string;
  teamId: string;
  title: string;
  date: string;
  time: string;
  prizePool: number;
  entryFee: number;
  type: string;
  slots: number;
  registered: number;
  status: 'UPCOMING' | 'ONGOING' | 'COMPLETED';
}

interface MatchesSectionProps {
  userId: string;
}

const MatchesSection: React.FC<MatchesSectionProps> = ({ userId }) => {
  const navigate = useNavigate();
  const [myMatches, setMyMatches] = useState<UserMatch[]>([]);
  const [matchFilter, setMatchFilter] = useState<'UPCOMING' | 'ONGOING' | 'COMPLETED'>('UPCOMING');
  const [loading, setLoading] = useState(true);

  const parseDateTime = (dateStr: string, timeStr: string) => {
    if (!dateStr || !timeStr) return Date.now();
    const [day, month, year] = dateStr.split('/');
    const [time, modifier] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    if (modifier === 'PM' && hours < 12) hours += 12;
    if (modifier === 'AM' && hours === 12) hours = 0;
    return new Date(Number(year), Number(month) - 1, Number(day), hours, minutes).getTime();
  };

  const formatINR = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  useEffect(() => {
    const fetchMatches = async () => {
      if (!userId) return;
      
      const db = getDatabase();
      const userMatchesRef = ref(db, `UserMatches/${userId}`);
      const snapshot = await get(userMatchesRef);
      
      if (!snapshot.exists()) {
        setMyMatches([]);
        setLoading(false);
        return;
      }
      
      const matchesData = snapshot.val();
      const matchesList: UserMatch[] = [];
      
      for (const [tournamentId, matchData] of Object.entries(matchesData)) {
        // If data is already stored with full info (optimized)
        if (typeof matchData === 'object' && matchData !== null && 'title' in matchData) {
          matchesList.push({
            tournamentId,
            game: (matchData as any).game || '',
            teamId: (matchData as any).teamId || '',
            title: (matchData as any).title || 'Unknown Tournament',
            date: (matchData as any).date || '',
            time: (matchData as any).time || '',
            prizePool: (matchData as any).prizePool || 0,
            entryFee: (matchData as any).entryFee || 0,
            type: (matchData as any).type || 'SQUAD',
            slots: (matchData as any).slots || 0,
            registered: (matchData as any).registered || 0,
            status: (matchData as any).status || 'UPCOMING',
          });
        } else {
          // Fallback for old data structure
          const teamId = matchData as string;
          const db = getDatabase();
          
          // Check BGMI tournaments
          const bgmiRef = ref(db, `Tournaments/BGMI/${tournamentId}`);
          const bgmiSnap = await get(bgmiRef);
          
          if (bgmiSnap.exists()) {
            const tournamentData = bgmiSnap.val();
            matchesList.push({
              tournamentId,
              game: 'BGMI',
              teamId,
              title: tournamentData.title || 'Unknown Tournament',
              date: tournamentData.date || '',
              time: tournamentData.time || '',
              prizePool: tournamentData.prizePool || 0,
              entryFee: tournamentData.joinOptions?.coinCost || 0,
              type: tournamentData.type || 'SQUAD',
              slots: tournamentData.slots || 0,
              registered: tournamentData.registered || 0,
              status: tournamentData.status || 'UPCOMING',
            });
          } else {
            // Check FFMAX tournaments
            const ffRef = ref(db, `Tournaments/FFMAX/${tournamentId}`);
            const ffSnap = await get(ffRef);
            if (ffSnap.exists()) {
              const tournamentData = ffSnap.val();
              matchesList.push({
                tournamentId,
                game: 'FFMAX',
                teamId,
                title: tournamentData.title || 'Unknown Tournament',
                date: tournamentData.date || '',
                time: tournamentData.time || '',
                prizePool: tournamentData.prizePool || 0,
                entryFee: tournamentData.joinOptions?.coinCost || 0,
                type: tournamentData.type || 'SQUAD',
                slots: tournamentData.slots || 0,
                registered: tournamentData.registered || 0,
                status: tournamentData.status || 'UPCOMING',
              });
            }
          }
        }
      }
      
      matchesList.sort((a, b) => {
        const statusOrder = { ONGOING: 0, UPCOMING: 1, COMPLETED: 2 };
        if (statusOrder[a.status] !== statusOrder[b.status]) {
          return statusOrder[a.status] - statusOrder[b.status];
        }
        const dateA = parseDateTime(a.date, a.time);
        const dateB = parseDateTime(b.date, b.time);
        return dateA - dateB;
      });
      
      setMyMatches(matchesList);
      setLoading(false);
    };
    
    fetchMatches();
  }, [userId]);

  const filteredMatches = myMatches.filter(match => match.status === matchFilter);

  if (loading) {
    return (
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-white font-rajdhani font-bold text-xl flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            My Battles
          </h2>
        </div>
        <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-8 text-center">
          <div className="animate-pulse">
            <div className="h-4 bg-purple-500/20 rounded w-3/4 mx-auto mb-4"></div>
            <div className="h-4 bg-purple-500/20 rounded w-1/2 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-white font-rajdhani font-bold text-xl flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          My Battles
        </h2>
      </div>

      <div className="flex gap-2 mb-4 overflow-x-auto hide-scrollbar">
        {(['UPCOMING', 'ONGOING', 'COMPLETED'] as const).map((tab) => (
          <motion.button
            key={tab}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setMatchFilter(tab)}
            className={`px-5 py-2 rounded-xl font-rajdhani font-bold text-sm transition-all duration-300 ${
              matchFilter === tab 
                ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg shadow-purple-500/25' 
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            {tab === 'ONGOING' ? (
              <span className="flex items-center gap-1">
                <Flame className="w-4 h-4" />
                LIVE
              </span>
            ) : tab === 'UPCOMING' ? (
              <span className="flex items-center gap-1">
                <Zap className="w-4 h-4" />
                UPCOMING
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                COMPLETED
              </span>
            )}
          </motion.button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {filteredMatches.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-12 text-center border border-purple-500/20"
          >
            <Gamepad2 className="w-16 h-16 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 font-rajdhani text-lg">No battles found</p>
            <p className="text-gray-500 text-sm mt-1">Join tournaments to see them here</p>
          </motion.div>
        ) : (
          <motion.div
            key={matchFilter}
            initial={{ opacity: 0, x: matchFilter === 'UPCOMING' ? -20 : 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: matchFilter === 'UPCOMING' ? 20 : -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-3"
          >
            {filteredMatches.map((match, idx) => (
              <motion.div
                key={match.tournamentId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="group relative bg-gradient-to-r from-[#1a1a2e] to-[#0a0a0f] rounded-xl overflow-hidden border border-white/10 hover:border-purple-500/50 transition-all duration-300"
              >
                <div className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <div className="text-3xl">
                        {match.game === 'BGMI' ? '🎮' : '🔥'}
                      </div>
                      <div>
                        <h3 className="text-white font-rajdhani font-bold text-base">{match.title}</h3>
                        <div className="flex items-center gap-2 text-xs mt-1">
                          {match.status === 'ONGOING' && (
                            <span className="flex items-center gap-1 text-red-400">
                              <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                              LIVE NOW
                            </span>
                          )}
                          {match.status === 'UPCOMING' && (
                            <span className="flex items-center gap-1 text-yellow-500">
                              <Clock className="w-3 h-3" />
                              {match.date} at {match.time}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={`px-2 py-1 rounded-lg text-xs font-rajdhani font-bold ${
                      match.game === 'BGMI' ? 'bg-blue-500/20 text-blue-400' : 'bg-orange-500/20 text-orange-400'
                    }`}>
                      {match.game}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <p className="text-gray-500 text-xs">Entry Fee</p>
                      <p className="text-purple-400 font-rajdhani font-bold text-sm">
                        {match.entryFee > 0 ? formatINR(match.entryFee) : 'FREE'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Prize Pool</p>
                      <p className="text-yellow-500 font-rajdhani font-bold text-sm">
                        {formatINR(match.prizePool)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Players</p>
                      <p className="text-white font-rajdhani font-bold text-sm">
                        {match.registered}/{match.slots}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Mode</p>
                      <p className="text-white font-rajdhani font-bold text-sm">{match.type}</p>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <div className="w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(match.registered / match.slots) * 100}%` }}
                        transition={{ duration: 0.8, delay: idx * 0.1 }}
                        className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full"
                      />
                    </div>
                  </div>
                  
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      if (match.status === 'COMPLETED') {
                        navigate(`/results/${match.game}/${match.tournamentId}`);
                      } else {
                        navigate(`/tournament/${match.game}/${match.tournamentId}`);
                      }
                    }}
                    className={`w-full py-2.5 rounded-xl text-white font-rajdhani font-bold text-sm transition-all duration-300 ${
                      match.status === 'ONGOING' 
                        ? 'bg-gradient-to-r from-red-500 to-red-600' 
                        : match.status === 'UPCOMING'
                        ? 'bg-gradient-to-r from-purple-500 to-blue-500'
                        : 'bg-white/10 text-gray-400'
                    }`}
                  >
                    {match.status === 'ONGOING' ? '⚔️ JOIN BATTLE' : 
                     match.status === 'UPCOMING' ? '📋 VIEW DETAILS' : 
                     '🏆 VIEW RESULTS'}
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MatchesSection;